import React, { useState, memo } from 'react';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import { ConstantPermissions } from 'contexts/useConstant';
// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';

import { Add, Assignment, Edit, Pages } from '@material-ui/icons';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import { cardTitle } from 'assets/jss/material-dashboard-pro-react.js';
import ReactTable from 'components/ReactTable/ReactTable.js';
import Button from 'components/CustomButtons/Button.js';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { Link } from 'react-router-dom';

import { Toasty, useToasty } from 'hooks/Toasty';
import { WithApp } from 'contexts/appContext';
import { wsFindBy } from 'services/WService';
import FilterArticle from 'hooks/FilterArticle';
import CardIcon from 'components/Card/CardIcon';
import { useModal } from 'hooks/useModal';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import FilterCustom from 'components/Tvendo/Common/FilterCustom';

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
    marginBottom: '0px'
  }
};
const useStyles = makeStyles(styles);

// Función para obtener columnas dinámicas de almacenes
function getWarehouseColumns(products) {
  const warehouses = [];
  const warehouseMap = new Map();
  
  products.forEach(product => {
    if (product.stocksview) {
      product.stocksview.forEach(stock => {
        if (!warehouseMap.has(stock.logi_warehouse_id)) {
          warehouseMap.set(stock.logi_warehouse_id, {
            id: stock.logi_warehouse_id,
            description: stock.logi_warehouse_description
          });
          warehouses.push({
            id: stock.logi_warehouse_id,
            description: stock.logi_warehouse_description
          });
        }
      });
    }
  });

  // Generar columnas dinámicas para cada almacén
  const dynamicColumns = [];
  warehouses.forEach(warehouse => {
    dynamicColumns.push(
      {
        Header: 'Almacén',
        accessor: `warehouse_${warehouse.id}`,
        width: 120
      },
      {
        Header: 'Stock',
        accessor: `warehouse_${warehouse.id}_stock`,
        width: 80
      },
      {
        Header: 'Retención',
        accessor: `warehouse_${warehouse.id}_retention`,
        width: 100
      }
    );
  });

  return dynamicColumns;
}

export default WithApp(function Product(props) {
  const classes = useStyles();
  const [filters, setFilters] = useState({});
  const [notify, showNotify] = useToasty();
  const [model, setOpen] = useModal();

  const [products, setProducts] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  async function listProducts(search) {
    setDataLoading(true);
    let response = await wsFindBy('logi/product-new', { ...search });
    setDataLoading(false);
    if (response.success) {
      setProducts(response.data);
      setFilters(search);
    }
  }

  // Columnas base (estáticas)
  const baseColumns = [
    { Header: '#', accessor: 'id', excelEnabled: false, width: 65 },
    { Header: 'Código', accessor: 'ItemCode', width: 110 },
    { Header: 'Nombre producto', accessor: 'ItemName', width: 220 },
    { Header: 'Cód. proveedor', accessor: 'CardCode', width: 150 },
    { Header: 'Proveedor', accessor: 'CardName', width: 180 },
    // { Header: 'Categoría', accessor: 'logi_category_description', width: 110 },
    // { Header: 'Línea', accessor: 'logi_line_description', width: 100 },
    { Header: 'Familia', accessor: 'logi_family_description', width: 180 },
    { Header: 'Fec. creación', accessor: 'created_at', width: 120 },
    // { Header: 'Estado', accessor: 'state', width: 90 }
  ];

  // Generar columnas dinámicas de almacenes
  const warehouseColumns = products.length > 0 ? getWarehouseColumns(products) : [];
  
  // Combinar todas las columnas
  const columns = [
    ...baseColumns,
    ...warehouseColumns,
    { Header: '', accessor: 'actions', excelEnabled: false, width: 0 }
  ];
  const totalTableWidth = columns.reduce((sum, col) => sum + (col.width || 0), 0);
  return (
    <GridContainer>
      <Toasty action={notify} />
      <GridItem xs={12}>
        <Card>
          <CardHeader icon>
            <CardIcon color={props.themeContext.buttonColor}>
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Listado de Productos {dataLoading && <LoadingSpinner />}</h4>
          </CardHeader>
          <CardBody>
            <FilterCustom getFilter={listProducts} provider dateRange diffRange={90}/>
            <ReactTable
              excelName="Productos_Nuevos"
              formatDate="DD-MM-YYYY"
              columns={columns}
              defaultWidth={totalTableWidth}
              data={products.map((product, i) => {
                // Objeto base con datos estáticos
                const rowData = {
                  id: (
                    <div className="actions-right">
                      {i + 1}
                    </div>
                  ),
                  ItemCode: product.ItemCode,
                  ItemName: product.ItemName,
                  CardCode: product.CardCode,
                  CardName: product.CardName,
                  logi_category_description: product.logi_category_description,
                  logi_family_description: product.logi_family_description,
                  logi_line_description: product.logi_line_description,
                  logi_mark_description: product.logi_mark_description,
                  created_at: product.created_at,
                  state: product.state === 'A' ? 'Activo' : 'Inactivo',
                  actions: '',
                };

                // Agregar datos dinámicos de almacenes
                if (product.stocksview) {
                  product.stocksview.forEach(stock => {
                    rowData[`warehouse_${stock.logi_warehouse_id}`] = stock.WhsName || 'No asignado';
                    rowData[`warehouse_${stock.logi_warehouse_id}_stock`] = parseFloat(stock.OnHand).toFixed(0) || 'No asignado';
                    rowData[`warehouse_${stock.logi_warehouse_id}_retention`] = parseFloat(stock.IsCommited).toFixed(0) || 'No asignado';
                  });
                }

                return rowData;
              })}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
});